import {
  UserGroupIcon,
  UserPlusIcon,
  UserMinusIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import Notification from "../components/Notification";
import UpgradePrompt from "../components/UpgradePrompt";
import { useAuth } from "../contexts/AuthContext";
import { useStudioPlan } from "../hooks/useStudioPlan";
import Tooltip from "../components/Tooltip";
import PaymentExpirationNotification from "../components/PaymentExpirationNotification";
import StudentPaymentExpirationBanner from "../components/StudentPaymentExpirationBanner";
import PaymentsByDescriptionCard from "../components/dashboard/PaymentsByDescriptionCard";
import OverduePaymentsCard from "../components/dashboard/OverduePaymentsCard";
import AdditionalMetricsCard from "../components/dashboard/AdditionalMetricsCard";
import AverageIncomeCard from "../components/dashboard/AverageIncomeCard";
import FamilyBillingAnnouncementModal from "../components/dashboard/FamilyBillingAnnouncementModal";
import {
  getLocalYMDFromUTC,
  getTodayInStudioTz,
  getLocalDateString,
  getWeekStartInStudioTz,
  getWeekDays,
  getLocalHourFromUTC,
} from "../utils/timezone";

// Security constants
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_PHONE_LENGTH = 20;
const SUBMISSION_COOLDOWN = 2000; // 2 seconds between API calls

/** Set to true to show Email/SMS "Birthdays" shortcuts in the dashboard header. */
const SHOW_DASHBOARD_BIRTHDAY_BUTTONS = false;

// Input sanitization function
const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  return sanitized.trim();
};

// Validate URL parameters
const validateStudioId = (studioId: string | null): boolean => {
  if (!studioId || typeof studioId !== 'string') return false;
  // UUID format validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(studioId);
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

// Data masking for sensitive information
const maskEmail = (email: string): string => {
  if (!email || email.length < 3) return email;
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  const maskedLocal = localPart.length > 2 
    ? localPart.substring(0, 2) + '*'.repeat(Math.max(0, localPart.length - 2))
    : localPart;
  return `${maskedLocal}@${domain}`;
};

const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 4) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return phone.substring(0, phone.length - 4) + '****';
};

// Sanitize student object
const sanitizeStudent = (student: any): Student => {
  return {
    ...student,
    name: sanitizeInput(student.name || '').substring(0, MAX_NAME_LENGTH),
    email: sanitizeInput(student.email || '').substring(0, MAX_EMAIL_LENGTH),
    phone: sanitizeInput(student.phone || '').substring(0, MAX_PHONE_LENGTH),
    belt_name: sanitizeInput(student.belt_name || '').substring(0, 100),
    membership_name: sanitizeInput(student.membership_name || '').substring(0, 100),
    status: sanitizeInput(student.status || '').substring(0, 50),
    // Validate numeric fields
    dropoff_risk: typeof student.dropoff_risk === 'number' && student.dropoff_risk >= 0 ? student.dropoff_risk : 0,
    ai_drop_off_risk: typeof student.ai_drop_off_risk === 'number' && student.ai_drop_off_risk >= 0 ? student.ai_drop_off_risk : 0,
  };
};

const DailyAttendanceChart = lazy(() =>
  import("../components/dashboard/DailyAttendanceChart")
);
const WeeklyAttendanceChart = lazy(() =>
  import("../components/dashboard/WeeklyAttendanceChart")
);
const TopClassesPie = lazy(() =>
  import("../components/dashboard/TopClassesPie")
);
const GenderDistributionPie = lazy(() =>
  import("../components/dashboard/GenderDistributionPie")
);
 

interface Student {
  id: string;
  name: string;
  belt_id: string;
  belt_name: string;
  membership_id: string;
  membership_name: string;
  studio_id: string;
  dropoff_risk: number;
  ai_drop_off_risk: number;
  email: string;
  phone: string;
  last_checked_in: string;
  status: string;
  membership_type_ids?: string[];
}

interface LeadSource {
  name: string;
  value: number;
}

interface LeadSourceCount {
  [key: string]: number;
}

interface NewMembersMetrics {
  newMembers: number;
  previousMonthCount: number;
  monthOverMonthGrowth: number | null;
}

const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Payments by Description moved to its own lazily-loaded component

const useDashboardData = () => {
  const { user, getAuthToken } = useAuth();

  const [activeStudents, setActiveStudents] = useState(0);
  const [inactiveStudents, setInactiveStudents] = useState(0);
  const [newMembersMetrics, setNewMembersMetrics] = useState<NewMembersMetrics>(
    {
      newMembers: 0,
      previousMonthCount: 0,
      monthOverMonthGrowth: null,
    }
  );
  // Removed Stripe metrics from eager fetch; cards fetch lazily
  const [financialMetrics, setFinancialMetrics] = useState({
    retentionRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isStripeConnected, setIsStripeConnected] = useState<boolean | null>(null);
  const [canUseStripeBilling, setCanUseStripeBilling] = useState(false);
  const [leadSourceData, setLeadSourceData] = useState<LeadSource[]>([]);
  const [genderDistributionData, setGenderDistributionData] = useState<
    LeadSource[]
  >([]);
  const [topClasses, setTopClasses] = useState<
    { name: string; attendance: number }[]
  >([]);
  const [leadsMetrics, setLeadsMetrics] = useState({
    leadsThisMonth: 0,
    leadsLastMonth: 0,
    monthOverMonthGrowth: null as number | null,
  });
  const [conversionRate, setConversionRate] = useState(0);
  const [highRiskStudents, setHighRiskStudents] = useState<Student[]>([]);
  const [riskThresholds, setRiskThresholds] = useState({
    medium: 0.2,
    high: 0.4,
  });
  const [studioName, setStudioName] = useState("");
  // Overdue data fetched lazily in its card
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [weeklyAttendanceData, setWeeklyAttendanceData] = useState<any[]>([]);
  const [birthdayCount, setBirthdayCount] = useState(0);
  const [studentsData, setStudentsData] = useState<any[]>([]);
  // Payments by description fetched lazily in its card
  const [studioId, setStudioId] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  useEffect(() => {
    const fetchAllData = async () => {
      const authToken = getAuthToken();
      if (!user || !authToken) return;

      // Rate limiting
      const now = Date.now();
      if (now - lastFetchTime < SUBMISSION_COOLDOWN) {
        return;
      }

      setLoading(true);
      setLastFetchTime(now);
      
      try {
        // 1. Fetch essential studio data first
        const studioResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        if (!studioResponse.ok) {
          logSecurityEvent('API_REQUEST_FAILED', { 
            status: studioResponse.status, 
            statusText: studioResponse.statusText,
            endpoint: 'studios/me'
          });
          throw new Error("Failed to fetch studio data");
        }
        const studioData = await studioResponse.json();
        
        // Validate and sanitize studio data
        const studioId = studioData.id;
        if (!validateStudioId(studioId)) {
          logSecurityEvent('INVALID_STUDIO_ID_RESPONSE', { studioId });
          throw new Error("Invalid studio ID received");
        }
        
        const studioTimezone = studioData.timezone || "America/New_York";
        const hasStripe = !!studioData.stripe_user_id;
        setIsStripeConnected(hasStripe);
        setCanUseStripeBilling(
          studioData.plan?.capabilities?.stripeBilling ?? false
        );

        // Sanitize studio name
        const sanitizedStudioName = sanitizeInput(studioData.name || 'Unknown Studio').substring(0, MAX_NAME_LENGTH);
        setStudioName(sanitizedStudioName);
        setStudioId(studioId);

        // 2. Prepare all parallel fetches
        const todayStr = getLocalDateString(studioTimezone, 0);
        const yestStr = getLocalDateString(studioTimezone, -1);
        const currentWeekStart = getWeekStartInStudioTz(studioTimezone);
        const previousWeekStart = new Date(currentWeekStart);
        previousWeekStart.setDate(previousWeekStart.getDate() - 7);

        const [
          studentsFirstPageRes,
          leadsFirstPageRes,
          classesRes,
          // paymentMetricsRes,
          // overduePaymentsRes,
          todayAttendanceRes,
          yestAttendanceRes,
          currentWeekAttendanceRes,
          previousWeekAttendanceRes,
          riskThresholdsRes,
          lastCheckedInRes,
        ] = await Promise.all([
          // Fetch all students with higher limit
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioId}?page=1&limit=10000`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
          // Fetch all leads with higher limit
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioId}?page=1&limit=10000`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${studioId}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
          // Stripe metrics and overdue are now lazy-fetched by cards
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/${studioId}?date=${todayStr}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/${studioId}?date=${yestStr}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
          Promise.all(
            getWeekDays(currentWeekStart).map((day) =>
              fetch(
                `${
                  process.env.REACT_APP_BACKEND_URL
                }/api/supabase/attendance/${studioId}?date=${day.toLocaleDateString(
                  "en-CA",
                  { timeZone: studioTimezone }
                )}`,
                {
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                  },
                }
              )
            )
          ),
          Promise.all(
            getWeekDays(previousWeekStart).map((day) =>
              fetch(
                `${
                  process.env.REACT_APP_BACKEND_URL
                }/api/supabase/attendance/${studioId}?date=${day.toLocaleDateString(
                  "en-CA",
                  { timeZone: studioTimezone }
                )}`,
                {
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                  },
                }
              )
            )
          ),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioId}/risk-thresholds`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
          // Fetch last checked-in dates from attendance records (most accurate)
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/last-checked-in/${studioId}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          ),
        ]);

        // 3. Process all data
        // Gather all students across all pages
        if (!studentsFirstPageRes.ok) {
          logSecurityEvent('API_REQUEST_FAILED', { 
            status: studentsFirstPageRes.status, 
            statusText: studentsFirstPageRes.statusText,
            endpoint: 'students'
          });
        }
        const studentsFirstPage = studentsFirstPageRes.ok
          ? await studentsFirstPageRes.json()
          : { data: [], pagination: { totalPages: 1, limit: 15 } };
        let allStudents = (studentsFirstPage.data || []).map(sanitizeStudent);
        const studentsTotalPages = studentsFirstPage.pagination?.totalPages || 1;
        const studentsLimit = studentsFirstPage.pagination?.limit || 15;
        if (studentsTotalPages > 1) {
          const studentPageFetches: Promise<Response>[] = [];
          for (let p = 2; p <= studentsTotalPages; p++) {
            studentPageFetches.push(
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioId}?page=${p}&limit=${studentsLimit}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
              )
            );
          }
          const studentPageResponses = await Promise.all(studentPageFetches);
          const studentPageJsons = await Promise.all(
            studentPageResponses.map((r) => r.json())
          );
          for (const pj of studentPageJsons) {
            const sanitizedPageStudents = (pj.data || []).map(sanitizeStudent);
            allStudents = allStudents.concat(sanitizedPageStudents);
          }
        }
        setStudentsData(allStudents);

        // Gather all leads across all pages
        const leadsFirstPage = leadsFirstPageRes.ok
          ? await leadsFirstPageRes.json()
          : { data: [], pagination: { totalPages: 1, limit: 100 } };
        let allLeads = leadsFirstPage.data || [];
        const leadsTotalPages = leadsFirstPage.pagination?.totalPages || 1;
        const leadsLimit = leadsFirstPage.pagination?.limit || 100;
        if (leadsTotalPages > 1) {
          const leadPageFetches: Promise<Response>[] = [];
          for (let p = 2; p <= leadsTotalPages; p++) {
            leadPageFetches.push(
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioId}?page=${p}&limit=${leadsLimit}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
              )
            );
          }
          const leadPageResponses = await Promise.all(leadPageFetches);
          const leadPageJsons = await Promise.all(
            leadPageResponses.map((r) => r.json())
          );
          for (const pj of leadPageJsons) {
            allLeads = allLeads.concat(pj.data || []);
          }
        }
        const leadsData = allLeads;
        const classesData = classesRes.ok ? await classesRes.json() : [];
        // Stripe metrics and overdue handled by lazy cards
        const todayAttendanceData = todayAttendanceRes.ok
          ? await todayAttendanceRes.json()
          : [];
        const yestAttendanceData = yestAttendanceRes.ok
          ? await yestAttendanceRes.json()
          : [];
        const currentWeekData = await Promise.all(
          currentWeekAttendanceRes.map((res) => (res.ok ? res.json() : []))
        );
        const previousWeekData = await Promise.all(
          previousWeekAttendanceRes.map((res) => (res.ok ? res.json() : []))
        );
        const riskThresholdsData = riskThresholdsRes.ok
          ? await riskThresholdsRes.json()
          : null;
        // Payments by description handled by lazy card

        // Process last checked-in data from attendance records (most accurate source)
        const lastCheckedInMap = lastCheckedInRes.ok
          ? await lastCheckedInRes.json()
          : {};

        // Process Risk Thresholds
        const newRiskThresholds = {
          medium: riskThresholdsData?.threshold
            ? riskThresholdsData.threshold / 2
            : 0.2,
          high: riskThresholdsData?.threshold ?? 0.4,
        };
        setRiskThresholds(newRiskThresholds);

        // No-op for payments by description

        // Process Students
        // Calculate status based on last_checked_in from attendance records (same logic as StudentManagement)
        // Active if checked in within last 2 weeks, otherwise Inactive
        let activeCount = 0;
        let inactiveCount = 0;
        if (Array.isArray(allStudents)) {
          allStudents.forEach((s: any) => {
            // Use lastCheckedInMap from attendance records (most accurate) instead of s.last_checked_in
            const lastCheckedIn = lastCheckedInMap[s.id] || s.last_checked_in;
            if (!lastCheckedIn) {
              inactiveCount++;
            } else {
              const lastCheckInDate = new Date(lastCheckedIn);
              const now = new Date();
              const weeksSinceLastCheckIn =
                (now.getTime() - lastCheckInDate.getTime()) /
                (1000 * 60 * 60 * 24 * 7);
              if (weeksSinceLastCheckIn <= 2) {
                activeCount++;
              } else {
                inactiveCount++;
              }
            }
          });
        }
        setActiveStudents(activeCount);
        setInactiveStudents(inactiveCount);

        // Process Birthdays - Count students with birthdays today
        const todayInStudio = getTodayInStudioTz(studioTimezone);
        const todayMonth = todayInStudio.getMonth() + 1; // JavaScript months are 0-indexed
        const todayDay = todayInStudio.getDate();

        const birthdaysToday = Array.isArray(allStudents)
          ? allStudents.filter((student: any) => {
              if (
                !student.date_of_birth &&
                !student.birthday &&
                !student.birthdate
              ) {
                return false;
              }

              // Try all possible field names
              const birthDate =
                student.date_of_birth || student.birthday || student.birthdate;
              if (!birthDate) return false;

              try {
                // Parse date in a timezone-neutral way to avoid timezone offset issues
                let birthMonth: number;
                let birthDay: number;

                if (birthDate.includes("-")) {
                  // Format: YYYY-MM-DD or YYYY-M-D
                  const parts = birthDate.split("-");
                  birthMonth = parseInt(parts[1], 10);
                  birthDay = parseInt(parts[2], 10);
                } else if (birthDate.includes("/")) {
                  // Format: MM/DD/YYYY or M/D/YYYY
                  const parts = birthDate.split("/");
                  birthMonth = parseInt(parts[0], 10);
                  birthDay = parseInt(parts[1], 10);
                } else {
                  // Fallback: create date at noon in studio timezone to avoid boundary issues
                  const birth = new Date(birthDate + "T12:00:00");
                  // Get month/day in studio timezone
                  const birthdayInStudio = new Date(
                    birth.toLocaleString("en-US", {
                      timeZone: studioTimezone,
                    })
                  );
                  birthMonth = birthdayInStudio.getMonth() + 1;
                  birthDay = birthdayInStudio.getDate();
                }

                return birthMonth === todayMonth && birthDay === todayDay;
              } catch (error) {
                return false;
              }
            }).length
          : 0;

        setBirthdayCount(birthdaysToday);

        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const currentDate = new Date();
        
        const newStudentsThisMonth = allStudents.filter(
          (student: any) => {
            // Prioritize join_date (explicit join date) over created_at (record creation)
            // This matches the database schema: join_date (date) and created_at (timestamp)
            const joinDateField = student.join_date || student.created_at;
            
            if (!joinDateField) return false;
            
            try {
              let joinDate;
              
              // Handle date-only strings (YYYY-MM-DD) more reliably
              if (typeof joinDateField === 'string' && joinDateField.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // For date-only strings, parse as local date to avoid timezone shifts
                const [year, month, day] = joinDateField.split('-').map(Number);
                joinDate = new Date(year, month - 1, day); // month is 0-based in JS Date
              } else {
                // For timestamp strings, use normal Date parsing
                joinDate = new Date(joinDateField);
              }
              
              // Check if date is valid
              if (isNaN(joinDate.getTime())) return false;
              
              const studentMonth = joinDate.getMonth();
              const studentYear = joinDate.getFullYear();
              
              return studentMonth === currentMonth && studentYear === currentYear;
            } catch (error) {
              return false;
            }
          }
        ).length;

        const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        const previousMonthNewStudents = allStudents.filter(
          (student: any) => {
            // Prioritize join_date (explicit join date) over created_at (record creation)
            // This matches the database schema: join_date (date) and created_at (timestamp)
            const joinDateField = student.join_date || student.created_at;
            if (!joinDateField) return false;
            
            try {
              let joinDate;
              
              // Handle date-only strings (YYYY-MM-DD) more reliably
              if (typeof joinDateField === 'string' && joinDateField.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // For date-only strings, parse as local date to avoid timezone shifts
                const [year, month, day] = joinDateField.split('-').map(Number);
                joinDate = new Date(year, month - 1, day); // month is 0-based in JS Date
              } else {
                // For timestamp strings, use normal Date parsing
                joinDate = new Date(joinDateField);
              }
              
              // Check if date is valid
              if (isNaN(joinDate.getTime())) return false;
              
              return (
                joinDate.getMonth() === previousMonth &&
                joinDate.getFullYear() === previousYear
              );
            } catch (error) {
              return false;
            }
          }
        ).length;

        const monthOverMonthGrowth = previousMonthNewStudents
          ? ((newStudentsThisMonth - previousMonthNewStudents) /
              previousMonthNewStudents) *
            100
          : null;

        setNewMembersMetrics({
          newMembers: newStudentsThisMonth,
          previousMonthCount: previousMonthNewStudents,
          monthOverMonthGrowth,
        });

        // Process Gender Distribution
        const formatGenderChartLabel = (key: string) => {
          if (key === "not assigned") return "Not Assigned";
          return key.charAt(0).toUpperCase() + key.slice(1);
        };
        const genderCount: { [key: string]: number } = {};
        if (Array.isArray(allStudents)) {
          allStudents.forEach((student: any) => {
            const gender = (student.gender || "not assigned").toLowerCase();
            genderCount[gender] = (genderCount[gender] || 0) + 1;
          });
        }
        const genderDistributionChartData = Object.entries(genderCount).map(
          ([name, value]) => ({
            name: formatGenderChartLabel(name),
            value,
          })
        );
        setGenderDistributionData(genderDistributionChartData);

        // Process Leads
        const leadsCreatedThisMonth = leadsData.filter((lead: any) => {
          const created = new Date(lead.created_at);
          return (
            created.getMonth() === currentMonth &&
            created.getFullYear() === currentYear
          );
        });
        const studentsConvertedFromLeadsThisMonth = allStudents.filter(
          (student: any) => {
            // converted_at is only set by converting a lead. Don't require
            // converted_from_lead_id: older conversions lost it when the lead
            // was deleted.
            if (!student.converted_at) return false;
            const convertedAt = new Date(student.converted_at);
            return (
              convertedAt.getMonth() === currentMonth &&
              convertedAt.getFullYear() === currentYear
            );
          }
        ).length;
        // Converting a lead deletes it, so count this month's conversions back
        // in; otherwise leads shrink as they convert and the rate can pass 100%.
        const leadsThisMonthCount =
          leadsCreatedThisMonth.length + studentsConvertedFromLeadsThisMonth;

        const monthlyConversionRate =
          leadsThisMonthCount > 0
            ? (studentsConvertedFromLeadsThisMonth / leadsThisMonthCount) * 100
            : 0;
        setConversionRate(monthlyConversionRate);

        const leadsLastMonthCount = leadsData.filter(
          (l: any) => new Date(l.created_at).getMonth() === previousMonth
        ).length;
        const leadsMonthOverMonthGrowth = leadsLastMonthCount
          ? ((leadsThisMonthCount - leadsLastMonthCount) /
              leadsLastMonthCount) *
            100
          : null;

        setLeadsMetrics({
          leadsThisMonth: leadsThisMonthCount,
          leadsLastMonth: leadsLastMonthCount,
          monthOverMonthGrowth: leadsMonthOverMonthGrowth,
        });

        // Process Lead Sources
        const sourceCount: LeadSourceCount = {};
        leadsData.forEach((lead: any) => {
          if (lead.source) {
            sourceCount[lead.source] = (sourceCount[lead.source] || 0) + 1;
          }
        });
        const leadSourceChartData = Object.entries(sourceCount).map(
          ([name, value]) => ({ name, value })
        );
        setLeadSourceData(leadSourceChartData);

        // Process Top Classes from actual attendance (last ~2 weeks of fetched data)
        const classNameById: Record<string, string> = {};
        if (Array.isArray(classesData)) {
          classesData.forEach((c: any) => {
            if (c.id && c.class_name) {
              classNameById[c.id] = c.class_name;
            }
          });
        }

        const seenAttendanceIds = new Set<string>();
        const classAttendanceCounts: Record<string, number> = {};

        const countClassAttendance = (records: unknown) => {
          if (!Array.isArray(records)) return;
          records.forEach((rec: any) => {
            if (!rec?.class_id || rec.event_id) return;
            if (rec.id && seenAttendanceIds.has(rec.id)) return;
            if (rec.id) seenAttendanceIds.add(rec.id);
            classAttendanceCounts[rec.class_id] =
              (classAttendanceCounts[rec.class_id] || 0) + 1;
          });
        };

        countClassAttendance(todayAttendanceData);
        countClassAttendance(yestAttendanceData);
        currentWeekData.forEach(countClassAttendance);
        previousWeekData.forEach(countClassAttendance);

        const topClassesData = Object.entries(classAttendanceCounts)
          .map(([classId, attendance]) => ({
            name: classNameById[classId] || "Unknown Class",
            attendance,
          }))
          .sort((a, b) => b.attendance - a.attendance)
          .slice(0, 5);
        setTopClasses(topClassesData);

        // Process High-Risk Students - Filter for high risk AND active memberships
        const highRisk = Array.isArray(allStudents)
          ? allStudents.filter((s: any) => {
              // Must meet high risk threshold
              if (s.ai_drop_off_risk < newRiskThresholds.high) return false;
              
              // Must have at least one active membership (membership_type_ids is the source of truth)
              // If they have membership IDs in the array, they have an active membership (cash or Stripe)
              return s.membership_type_ids && Array.isArray(s.membership_type_ids) && s.membership_type_ids.length > 0;
            })
          : [];
        // Note: allStudents are already sanitized above, so highRisk students are also sanitized
        setHighRiskStudents(highRisk);

        // Financial metrics populated lazily

        // Process Daily Attendance Chart
        const hourlyCounts: {
          [key: string]: { current: number; previous: number };
        } = {};
        for (let i = 0; i < 24; i++) {
          hourlyCounts[i.toString().padStart(2, "0")] = {
            current: 0,
            previous: 0,
          };
        }
        todayAttendanceData.forEach((record: any) => {
          if (record.class_id && !record.event_id) {
            const localHour = getLocalHourFromUTC(
              record.attended_at,
              studioTimezone
            );
            hourlyCounts[localHour.toString().padStart(2, "0")].current++;
          }
        });
        yestAttendanceData.forEach((record: any) => {
          if (record.class_id && !record.event_id) {
            const localHour = getLocalHourFromUTC(
              record.attended_at,
              studioTimezone
            );
            hourlyCounts[localHour.toString().padStart(2, "0")].previous++;
          }
        });
        const dailyChartData = Object.entries(hourlyCounts)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([hour, counts]) => {
            const hourNum = parseInt(hour, 10);
            const date = new Date();
            date.setUTCHours(hourNum, 0, 0, 0);
            const timeLabel = date.toLocaleTimeString("en-US", {
              hour: "numeric",
              hour12: true,
              timeZone: "UTC",
            });
            return {
              time: timeLabel,
              current: counts.current,
              previous: counts.previous,
            };
          });
        setAttendanceData(dailyChartData);

        // Process Weekly Attendance Chart
        const processWeeklyData = (data: any[], weekStart: Date) => {
          const weekDays = getWeekDays(weekStart);
          return weekDays.map((day, index) => {
            const dailyData = data[index];
            if (!Array.isArray(dailyData)) return 0;
            const targetYMD = day.toLocaleDateString("en-CA");
            return dailyData.filter((rec: any) => {
              if (!(rec.class_id && !rec.event_id)) return false;
              const localYMD = getLocalYMDFromUTC(
                rec.attended_at,
                studioTimezone
              );
              return localYMD === targetYMD;
            }).length;
          });
        };

        const currentCounts = processWeeklyData(
          currentWeekData,
          currentWeekStart
        );
        const previousCounts = processWeeklyData(
          previousWeekData,
          previousWeekStart
        );

        const weeklyChartData = dayLabels.map((day, idx) => ({
          day,
          current: currentCounts[idx] || 0,
          previous: previousCounts[idx] || 0,
        }));
        setWeeklyAttendanceData(weeklyChartData);
      } catch (error) {
        logSecurityEvent('DASHBOARD_DATA_FETCH_ERROR', { 
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        // Failed to fetch dashboard data
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user, getAuthToken, lastFetchTime]);

  const churnRate = useMemo(() => {
    const totalStudents = activeStudents + inactiveStudents;
    if (totalStudents === 0) return 0;
    return (inactiveStudents / totalStudents) * 100;
  }, [activeStudents, inactiveStudents]);

  const retentionRate = useMemo(() => {
    const totalStudents = activeStudents + inactiveStudents;
    if (totalStudents === 0) return 0;
    return (activeStudents / totalStudents) * 100;
  }, [activeStudents, inactiveStudents]);

  return {
    loading,
    studioId,
    studioName,
    activeStudents,
    inactiveStudents,
    newMembersMetrics,
    leadsMetrics,
    financialMetrics,
    highRiskStudents,
    riskThresholds,
    attendanceData,
    weeklyAttendanceData,
    topClasses,
    genderDistributionData,
    leadSourceData,
    churnRate,
    retentionRate,
    conversionRate,
    birthdayCount,
    studentsData,
    isStripeConnected,
    canUseStripeBilling,
  };
};

function DashboardPlanLoadingCard({
  title,
  variant = "default",
}: {
  title?: string;
  variant?: "default" | "attention";
}) {
  const cardClass =
    variant === "attention"
      ? "from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border-red-100 dark:border-red-800"
      : "from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800 border-gray-100 dark:border-gray-700";

  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-br ${cardClass} border-2 shadow-md rounded-xl px-4 py-5 sm:px-6 sm:py-6`}
    >
      {title ? (
        <h3 className="text-lg font-bold font-sans leading-6 text-gray-900 dark:text-white mb-4">
          {title}
        </h3>
      ) : null}
      <div className="flex justify-center items-center py-8">
        <div className="animate-pulse flex space-x-4 items-center">
          <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
          <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    </div>
  );
}

function DashboardAttentionSectionSkeleton({
  showOutstanding,
  showAtRisk,
}: {
  showOutstanding: boolean;
  showAtRisk: boolean;
}) {
  if (!showOutstanding && !showAtRisk) return null;

  return (
    <div
      className={`grid grid-cols-1 gap-6 mt-8${
        showOutstanding && showAtRisk ? " xl:grid-cols-2" : ""
      }`}
    >
      {showOutstanding && (
        <DashboardPlanLoadingCard
          variant="attention"
          title="Students With Outstanding Payments"
        />
      )}
      {showAtRisk && (
        <DashboardPlanLoadingCard
          variant="attention"
          title="At-Risk Active Members"
        />
      )}
    </div>
  );
}

function DashboardAnalyticsSkeleton() {
  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-8">
        <DashboardPlanLoadingCard title="Total Payments" />
        <DashboardPlanLoadingCard title="Additional Metrics" />
        <DashboardPlanLoadingCard title="Average Student Membership Income" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-8">
        <DashboardPlanLoadingCard title="Daily Class Attendance by Time" />
        <DashboardPlanLoadingCard title="Weekly Class Attendance Trend" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-8">
        <DashboardPlanLoadingCard title="Top Attended Classes Last 30 Days" />
        <DashboardPlanLoadingCard title="Student Gender Distribution" />
        <DashboardPlanLoadingCard title="Top Lead Sources" />
      </div>
    </>
  );
}

function DashboardChartLoading({ label = "Loading chart..." }: { label?: string }) {
  return (
    <div className="flex justify-center items-center h-80">
      <div className="animate-pulse flex space-x-4 items-center">
        <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
        <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const {
    loading,
    studioId,
    studioName,
    activeStudents,
    inactiveStudents,
    newMembersMetrics,
    leadsMetrics,
    financialMetrics,
    highRiskStudents,
    riskThresholds,
    attendanceData,
    weeklyAttendanceData,
    topClasses,
    genderDistributionData,
    leadSourceData,
    churnRate,
    retentionRate,
    conversionRate,
    birthdayCount,
    studentsData,
    isStripeConnected,
  } = useDashboardData();

  const navigate = useNavigate();
  const { getAuthToken, user, hasPermission } = useAuth();
  const {
    hasCapability,
    loading: planLoading,
    canUseStripeBilling,
  } = useStudioPlan();
  const showFullAnalytics = hasCapability('dashboardFullAnalytics');
  const showDropoffRisk = hasCapability('aiDropoffRisk');
  const showUpgradeBesideAtRisk =
    !planLoading && !showFullAnalytics && !canUseStripeBilling && showDropoffRisk;

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Helper function to get students with birthdays today
  const getBirthdayStudentsToday = () => {
    if (!Array.isArray(studentsData) || studentsData.length === 0) return [];

    // Get today's date in the studio timezone
    const studioTimezone = "America/Denver"; // This should ideally come from studio data
    const todayInStudio = getTodayInStudioTz(studioTimezone);
    const todayMonth = todayInStudio.getMonth() + 1;
    const todayDay = todayInStudio.getDate();

    // Filter students with birthdays today
    return studentsData
      .filter((student) => {
        if (!student.date_of_birth && !student.birthday && !student.birthdate)
          return false;

        const birthDate =
          student.date_of_birth || student.birthday || student.birthdate;
        if (!birthDate) return false;

        try {
          let birthMonth: number;
          let birthDay: number;

          if (birthDate.includes("-")) {
            const parts = birthDate.split("-");
            birthMonth = parseInt(parts[1], 10);
            birthDay = parseInt(parts[2], 10);
          } else if (birthDate.includes("/")) {
            const parts = birthDate.split("/");
            birthMonth = parseInt(parts[0], 10);
            birthDay = parseInt(parts[1], 10);
          } else {
            const birth = new Date(birthDate + "T12:00:00");
            const birthdayInStudio = new Date(
              birth.toLocaleString("en-US", {
                timeZone: studioTimezone,
              })
            );
            birthMonth = birthdayInStudio.getMonth() + 1;
            birthDay = birthdayInStudio.getDate();
          }

          return birthMonth === todayMonth && birthDay === todayDay;
        } catch {
          return false;
        }
      });
  };

  const handleBirthdayClick = () => {
    const birthdayStudentsToday = getBirthdayStudentsToday();
    
    // Get their info including birthday date and email
    const birthdayStudents = birthdayStudentsToday
      .map((student) => {
        const birthDate = student.date_of_birth || student.birthday || student.birthdate;
        return {
          name: student.name,
          email: student.email,
          birthday: birthDate,
        };
      })
      .filter((student) => student.email && student.email.trim() !== ""); // Only include students with valid emails

    // Extract emails for backward compatibility
    const birthdayStudentEmails = birthdayStudents.map((s) => s.email);

    // Navigate to messaging with the birthday students data (including emails and birthdays)
    navigate("/app/messaging", {
      state: { 
        birthdayEmails: birthdayStudentEmails,
        birthdayStudents: birthdayStudents, // Include full student data with birthdays
      },
    });
  };

  const handleBirthdaySMSClick = () => {
    const birthdayStudentsToday = getBirthdayStudentsToday();
    
    // Get their info including birthday date and phone
    const birthdayStudents = birthdayStudentsToday
      .map((student) => {
        const birthDate = student.date_of_birth || student.birthday || student.birthdate;
        return {
          name: student.name,
          phone: student.phone,
          birthday: birthDate,
        };
      })
      .filter((student) => student.phone && student.phone.trim() !== ""); // Only include students with valid phones

    // Extract phone numbers
    const birthdayStudentPhones = birthdayStudents.map((s) => s.phone);

    // Navigate to SMS with the birthday students data (including phones and birthdays)
    navigate("/app/sms", {
      state: { 
        birthdayPhones: birthdayStudentPhones,
        birthdayStudents: birthdayStudents, // Include full student data with birthdays
      },
    });
  };

  const [showWelcomeNotification, setShowWelcomeNotification] = useState(false);
  const [showPaymentNotification, setShowPaymentNotification] = useState(false);
  const [showStudentPaymentBanner, setShowStudentPaymentBanner] = useState(false);
  const [highRiskCurrentPage, setHighRiskCurrentPage] = useState(1);
  const highRiskItemsPerPage = 5;
  const highRiskTotalPages = Math.ceil(
    highRiskStudents.length / highRiskItemsPerPage
  );
  // Test Missed Appointments button - commented out for production
  // const [testingMissedAppointments, setTestingMissedAppointments] = useState(false);

  // const handleTestMissedAppointments = async () => {
  //   try {
  //     setTestingMissedAppointments(true);
  //     const authToken = getAuthToken();
  //     if (!authToken) {
  //       setNotification({
  //         show: true,
  //         message: "Authentication token not found",
  //         title: "Error",
  //       });
  //       return;
  //     }

  //     const response = await fetch(
  //       `${process.env.REACT_APP_BACKEND_URL}/api/notifications/debug/check-missed-appointments`,
  //       {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           Authorization: `Bearer ${authToken}`,
  //         },
  //       }
  //     );

  //     if (response.ok) {
  //       const data = await response.json();
  //       setNotification({
  //         show: true,
  //         message: data.message || "Missed appointments check completed. Refresh notifications to see results.",
  //         title: "Success",
  //       });
  //     } else {
  //       const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
  //       setNotification({
  //         show: true,
  //         message: errorData.error || "Failed to check missed appointments",
  //         title: "Error",
  //       });
  //     }
  //   } catch (error) {
  //     setNotification({
  //       show: true,
  //       message: error instanceof Error ? error.message : "Failed to check missed appointments",
  //       title: "Error",
  //     });
  //   } finally {
  //     setTestingMissedAppointments(false);
  //   }
  // };


  useEffect(() => {
    // Check if this is the first time after onboarding
    const isFirstTimeAfterOnboarding = sessionStorage.getItem(
      "justCompletedOnboarding"
    );
    if (isFirstTimeAfterOnboarding) {
      setShowWelcomeNotification(true);
      sessionStorage.removeItem("justCompletedOnboarding");
    }
  }, []);

  // welcomeMessage unused (left intentionally for future use)

  // No-op

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <FamilyBillingAnnouncementModal
        // Hidden on login for now; still available from the Updates page.
        enabled={
          false &&
          !planLoading &&
          canUseStripeBilling &&
          isStripeConnected === true &&
          hasPermission("students")
        }
        studioId={studioId}
        viewerId={user?.id || null}
      />
      <PaymentExpirationNotification 
        onClose={() => setShowPaymentNotification(false)}
      />
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-2">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          {SHOW_DASHBOARD_BIRTHDAY_BUTTONS && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleBirthdayClick}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <EnvelopeIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
                <span className="pointer-events-none">
                  Birthdays{" "}
                  {loading ? (
                    <span className="inline-block w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-600">
                      &nbsp;
                    </span>
                  ) : (
                    birthdayCount
                  )}
                </span>
              </button>
              <button
                onClick={handleBirthdaySMSClick}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PhoneIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
                <span className="pointer-events-none">
                  Birthdays{" "}
                  {loading ? (
                    <span className="inline-block w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-600">
                      &nbsp;
                    </span>
                  ) : (
                    birthdayCount
                  )}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Student Payment Expiration Banner */}
        <StudentPaymentExpirationBanner onClose={() => setShowStudentPaymentBanner(false)} />


        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-4 mt-2">
          {/* First Row - Active/Inactive Students */}
          <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 px-4 py-5 border-2 border-green-100 dark:border-green-800 shadow-md sm:px-6 sm:py-6 rounded-xl hover:shadow-lg hover:-translate-y-1 transition">
            <dt>
              <div className="absolute rounded-lg bg-green-500 p-3 ring-4 ring-green-100 dark:ring-green-900">
                <UserGroupIcon
                  className="h-8 w-8 text-white"
                  aria-hidden="true"
                />
              </div>
              <p className="ml-20 truncate text-sm font-semibold text-gray-700 dark:text-gray-300">
                Total Active Students
              </p>
            </dt>
            <dd className="ml-20 flex items-baseline">
              <p className="text-3xl font-extrabold font-sans text-gray-900 dark:text-white">
                {loading ? (
                  <span className="inline-block w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-600">
                    &nbsp;
                  </span>
                ) : (
                  activeStudents
                )}
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 px-4 py-5 border-2 border-red-100 dark:border-red-800 shadow-md sm:px-6 sm:py-6 rounded-xl hover:shadow-lg hover:-translate-y-1 transition">
            <dt>
              <div className="absolute rounded-lg bg-red-500 p-3 ring-4 ring-red-100 dark:ring-red-900">
                <UserMinusIcon
                  className="h-8 w-8 text-white"
                  aria-hidden="true"
                />
              </div>
              <p className="ml-20 truncate text-sm font-semibold text-gray-700 dark:text-gray-300">
                Total Inactive Students
              </p>
            </dt>
            <dd className="ml-20 flex items-baseline">
              <p className="text-3xl font-extrabold font-sans text-gray-900 dark:text-white">
                {loading ? (
                  <span className="inline-block w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-600">
                    &nbsp;
                  </span>
                ) : (
                  inactiveStudents
                )}
              </p>
            </dd>
          </div>

          {/* Second Row - New Students/Leads */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md sm:px-6 sm:py-6 rounded-xl hover:shadow-lg hover:-translate-y-1 transition">
            <dt>
              <div className="absolute rounded-lg bg-indigo-500 p-3 ring-4 ring-indigo-100 dark:ring-indigo-900">
                <UserPlusIcon
                  className="h-8 w-8 text-white"
                  aria-hidden="true"
                />
              </div>
              <p className="ml-20 truncate text-sm font-semibold text-gray-700 dark:text-gray-300">
                New Students This Month
              </p>
            </dt>
            <dd className="ml-20 flex flex-col items-baseline">
              {loading ? (
                <div className="h-8 w-28 animate-pulse bg-gray-200 dark:bg-gray-600 rounded"></div>
              ) : (
                <div>
                  <p className="text-3xl font-extrabold font-sans text-gray-900 dark:text-white">
                    {newMembersMetrics.newMembers}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Previous Month: {newMembersMetrics.previousMonthCount ?? 0}
                  </p>
                  {newMembersMetrics.monthOverMonthGrowth !== null && (
                    <p className="text-sm mt-2 flex items-center gap-1">
                      {newMembersMetrics.monthOverMonthGrowth >= 0 ? (
                        <span className="text-green-600 dark:text-green-400">
                          ↑
                        </span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">
                          ↓
                        </span>
                      )}
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded ${
                          newMembersMetrics.monthOverMonthGrowth >= 0
                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                            : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                        }`}
                      >
                        {Math.abs(
                          newMembersMetrics.monthOverMonthGrowth
                        ).toFixed(1)}
                        %
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">
                        vs last month
                      </span>
                    </p>
                  )}
                </div>
              )}
            </dd>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md sm:px-6 sm:py-6 rounded-xl hover:shadow-lg hover:-translate-y-1 transition">
            <dt>
              <div className="absolute rounded-lg bg-blue-500 p-3 ring-4 ring-blue-100 dark:ring-blue-900">
                <UserPlusIcon
                  className="h-8 w-8 text-white"
                  aria-hidden="true"
                />
              </div>
              <p className="ml-20 truncate text-sm font-semibold text-gray-700 dark:text-gray-300">
                New Leads This Month
              </p>
            </dt>
            <dd className="ml-20 flex flex-col items-baseline">
              {loading ? (
                <div className="h-8 w-28 animate-pulse bg-gray-200 dark:bg-gray-600 rounded"></div>
              ) : (
                <div>
                  <p className="text-3xl font-extrabold font-sans text-gray-900 dark:text-white">
                    {leadsMetrics.leadsThisMonth}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Previous Month: {leadsMetrics.leadsLastMonth ?? 0}
                  </p>
                  {leadsMetrics.monthOverMonthGrowth !== null && (
                    <p className="text-sm flex items-center gap-1 mt-1">
                      {leadsMetrics.monthOverMonthGrowth >= 0 ? (
                        <span className="text-green-600 dark:text-green-400">
                          ↑
                        </span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">
                          ↓
                        </span>
                      )}
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded ${
                          leadsMetrics.monthOverMonthGrowth >= 0
                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                            : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                        }`}
                      >
                        {Math.abs(leadsMetrics.monthOverMonthGrowth).toFixed(1)}
                        %
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">
                        vs last month
                      </span>
                    </p>
                  )}
                </div>
              )}
            </dd>
          </div>
        </div>


        {(planLoading || canUseStripeBilling || showDropoffRisk) &&
          (showUpgradeBesideAtRisk && loading ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-8">
              <DashboardPlanLoadingCard
                variant="attention"
                title="At-Risk Active Members"
              />
              <UpgradePrompt
                capability="dashboardFullAnalytics"
                className="h-full rounded-xl"
              />
            </div>
          ) : planLoading || loading ? (
            <DashboardAttentionSectionSkeleton
              showOutstanding={!planLoading && canUseStripeBilling}
              showAtRisk={planLoading || showDropoffRisk}
            />
          ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-8">
          {canUseStripeBilling && (
            <OverduePaymentsCard
              studioId={studioId}
              isStripeConnected={isStripeConnected}
              canUseStripeBilling={canUseStripeBilling}
            />
          )}

          {showDropoffRisk && (
          <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border-2 border-red-100 dark:border-red-800 shadow-md rounded-xl px-4 py-5 sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold font-sans leading-6 text-gray-900 dark:text-white">
                  At-Risk Active Members
                </h3>
                <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold px-2 py-1 rounded">
                  Requires Attention
                </span>
              </div>
              <div className="mt-2 max-w-xl text-sm text-gray-500 dark:text-gray-400">
                {loading ? (
                  <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4 animate-pulse"></div>
                ) : (
                  <p>
                    Members with an active membership and retention risk of{" "}
                    {`${(riskThresholds.high * 100).toFixed(0)}%`} or higher.
                    Prioritize outreach before they disengage or cancel.
                  </p>
                )}
              </div>
              <div className="mt-4">
                {loading ? (
                  <div className="flex justify-center items-center py-4">
                    <div className="animate-pulse flex space-x-4 items-center">
                      <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Loading high-risk students...
                      </div>
                    </div>
                  </div>
                ) : highRiskStudents.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No at-risk active members found.
                  </p>
                ) : (
                  <div className="overflow-x-auto overflow-y-hidden -mx-2 px-2 xl:mx-0 xl:px-0" style={{ scrollbarWidth: 'thin' }}>
                    <div className="min-w-[600px] xl:min-w-0">
                  <div className="relative overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                      <thead>
                        <tr>
                          <th className="py-2 pl-4 pr-3 text-left font-bold font-sans text-gray-900 dark:text-white sm:pl-6 w-1/3">
                            Name
                          </th>
                          <th className="px-3 py-2 text-left font-bold font-sans text-gray-900 dark:text-white w-1/3">
                            Contact
                          </th>
                          <th className="px-3 py-2 text-left font-bold font-sans text-gray-900 dark:text-white w-1/3">
                            Retention Risk
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {highRiskStudents
                          .slice(
                            (highRiskCurrentPage - 1) * 5,
                            highRiskCurrentPage * 5
                          )
                          .map((student) => (
                            <tr key={student.id} className="align-middle">
                              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-xs font-medium text-gray-900 dark:text-white sm:pl-6">
                                <div className="flex items-center gap-2">
                                  <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                  {student.name}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-1">
                                    <EnvelopeIcon className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                    {student.email ? (
                                      <span title="Click student name to access full contact info">{maskEmail(student.email)}</span>
                                    ) : (
                                      <span className="italic text-gray-300 dark:text-gray-600">
                                        No email
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <PhoneIcon className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                                    {student.phone ? (
                                      <span title="Click student name to access full contact info">{maskPhone(student.phone)}</span>
                                    ) : (
                                      <span className="italic text-gray-300 dark:text-gray-600">
                                        No phone
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-xs">
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${
                                        student.ai_drop_off_risk <
                                        riskThresholds.medium
                                          ? "bg-green-500"
                                          : student.ai_drop_off_risk <
                                            riskThresholds.high
                                          ? "bg-yellow-500"
                                          : "bg-red-500"
                                      }`}
                                      style={{
                                        width: `${
                                          student.ai_drop_off_risk * 100
                                        }%`,
                                      }}
                                    />
                                  </div>
                                  <span className="font-medium">
                                    {typeof student.ai_drop_off_risk ===
                                    "number"
                                      ? (
                                          student.ai_drop_off_risk * 100
                                        ).toFixed(0) + "%"
                                      : "N/A"}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {/* Pagination for high risk students */}
                    {highRiskTotalPages > 1 && (
                      <div className="flex justify-between items-center mt-2">
                        <button
                          className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 text-xs"
                          onClick={() =>
                            setHighRiskCurrentPage((p) => Math.max(1, p - 1))
                          }
                          disabled={highRiskCurrentPage === 1}
                        >
                          <span className="pointer-events-none">Previous</span>
                        </button>
                        <div className="space-x-1">
                          {Array.from(
                            {
                              length: highRiskTotalPages,
                            },
                            (_, i) => (
                              <button
                                key={i + 1}
                                className={`px-2 py-1 rounded text-xs ${
                                  highRiskCurrentPage === i + 1
                                    ? "bg-indigo-600 text-white"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }`}
                                onClick={() => setHighRiskCurrentPage(i + 1)}
                              >
                                <span className="pointer-events-none">{i + 1}</span>
                              </button>
                            )
                          )}
                        </div>
                        <button
                          className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 text-xs"
                          onClick={() =>
                            setHighRiskCurrentPage((p) =>
                              Math.min(highRiskTotalPages, p + 1)
                            )
                          }
                          disabled={highRiskCurrentPage === highRiskTotalPages}
                        >
                          <span className="pointer-events-none">Next</span>
                        </button>
                      </div>
                    )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {showUpgradeBesideAtRisk && (
            <UpgradePrompt
              capability="dashboardFullAnalytics"
              className="h-full rounded-xl"
            />
          )}
        </div>
          ))}

        {planLoading || showUpgradeBesideAtRisk ? null : !showFullAnalytics ? (
          <div className="mt-8">
            <UpgradePrompt capability="dashboardFullAnalytics" />
          </div>
        ) : loading ? (
          <DashboardAnalyticsSkeleton />
        ) : (
        <>
        {/* New Metrics Section */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-8">
          {/* Payments by Product Description */}
          <PaymentsByDescriptionCard
            studioId={studioId}
            isStripeConnected={isStripeConnected}
            canUseStripeBilling={canUseStripeBilling}
          />

          {/* Financial Metrics */}
          <AdditionalMetricsCard
            studioId={studioId}
            isStripeConnected={isStripeConnected}
            canUseStripeBilling={canUseStripeBilling}
            retentionRate={retentionRate}
            churnRate={churnRate}
          />

          {/* Average Membership Income */}
          <AverageIncomeCard
            studioId={studioId}
            isStripeConnected={isStripeConnected}
            canUseStripeBilling={canUseStripeBilling}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-8">
          {/* Daily Attendance by Time */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Daily Class Attendance by Time
            </h3>
            <Suspense
              fallback={<DashboardChartLoading />}
            >
              <DailyAttendanceChart loading={false} data={attendanceData as any} />
            </Suspense>
          </div>

          {/* Weekly Attendance Trend */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Weekly Class Attendance Trend
            </h3>
            <Suspense
              fallback={<DashboardChartLoading />}
            >
              <WeeklyAttendanceChart loading={false} data={weeklyAttendanceData as any} />
            </Suspense>
          </div>
        </div>

        {/* Top Attended Classes, Gender Distribution, and Top Lead Sources */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-8">
          {/* Top Attended Classes Last 30 Days */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Top Attended Classes Last 30 Days
            </h3>
            <Suspense fallback={<DashboardChartLoading />}>
              <TopClassesPie loading={false} data={topClasses as any} />
            </Suspense>
          </div>

          {/* Student Gender Distribution */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              <Tooltip text="Students without a gender assignment in Student Management will appear as Not Assigned in this distribution.">
                <span className="cursor-help">Student Gender Distribution</span>
              </Tooltip>
            </h3>
            <Suspense
              fallback={<DashboardChartLoading label="Loading chart..." />}
            >
              <GenderDistributionPie loading={false} data={genderDistributionData as any} />
            </Suspense>
          </div>

          {/* Top Lead Sources */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Top Lead Sources
            </h3>
            <ul className="space-y-2">
              {(() => {
                if (loading) {
                  return (
                    <li className="flex justify-center items-center py-4">
                      <div className="animate-pulse flex space-x-4 items-center">
                        <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Loading lead sources...
                        </div>
                      </div>
                    </li>
                  );
                }
                if (leadSourceData.length === 0) {
                  return (
                    <li className="text-gray-400 dark:text-gray-500 italic">
                      No lead sources
                    </li>
                  );
                }
                const total = leadSourceData.reduce(
                  (sum, s) => sum + s.value,
                  0
                );
                // Sort by percent descending
                const sorted = [...leadSourceData].sort(
                  (a, b) => b.value / total - a.value / total
                );
                return sorted.map((source) => {
                  const percent = total > 0 ? (source.value / total) * 100 : 0;
                  return (
                    <li
                      key={source.name}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {source.name}
                      </span>
                      <span className="ml-auto text-gray-500 dark:text-gray-400">
                        {source.value}
                      </span>
                      <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                        {percent.toFixed(1)}%
                      </span>
                    </li>
                  );
                });
              })()}
            </ul>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
